import React, { PropsWithChildren, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

interface Props {
    itemCount: number;
    scrollRef: React.MutableRefObject<HTMLElement>;
    stretchToFill: boolean;
}

const GradientScrollContainer = ({
    children,
    itemCount, // to recalculate gradient visibility if itemCount changes
    scrollRef,
    stretchToFill
}: PropsWithChildren<Props>) => {
    const [showLeftGradient, setShowLeftGradient] = useState(false);
    const [showRightGradient, setShowRightGradient] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (!scrollRef.current) {
                return;
            }
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            setShowLeftGradient(scrollLeft > 0);
            setShowRightGradient(
                scrollWidth > clientWidth &&
                    scrollLeft < scrollWidth - clientWidth
            );
        };

        const scrollElement = scrollRef.current;
        if (scrollElement) {
            scrollElement.addEventListener('scroll', handleScroll);
            handleScroll(); // Initialize gradient visibility on mount
        }
        return () => {
            if (scrollElement) {
                scrollElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [itemCount, scrollRef]);

    return (
        <Container
            showLeftGradient={showLeftGradient}
            showRightGradient={showRightGradient}
            stretchToFill={stretchToFill}
        >
            {children}
        </Container>
    );
};

export default GradientScrollContainer;

// -------------------------------- Styles -------------------------------- //
const FADE_WIDTH = 50;

// In CSS masks, 'black' (any opaque color) = fully visible, 'transparent' = hidden.
// This allows the fade to work on any background color.
const getMaskImage = (showLeft: boolean, showRight: boolean) => {
    if (!showLeft && !showRight) {
        return 'none';
    }

    const leftStart = showLeft ? 'transparent' : 'black';
    const rightEnd = showRight ? 'transparent' : 'black';

    return `linear-gradient(to right, ${leftStart}, black ${FADE_WIDTH}px, black calc(100% - ${FADE_WIDTH}px), ${rightEnd})`;
};

const Container = styled.div<{
    showLeftGradient: boolean;
    showRightGradient: boolean;
    stretchToFill: boolean;
}>`
    position: relative;
    overflow-x: auto;
    white-space: nowrap;
    ${({ stretchToFill }) =>
        stretchToFill &&
        css`
            width: 100%;
        `}

    mask-image: ${props =>
        getMaskImage(props.showLeftGradient, props.showRightGradient)};
    -webkit-mask-image: ${props =>
        getMaskImage(props.showLeftGradient, props.showRightGradient)};
`;
